import React, { useEffect, useState } from "react";
import { Cookie, X, Shield, BarChart3, Megaphone, ChevronDown } from "lucide-react";

const STORAGE_KEY = "abh_cookie_consent_v1";
const CONSENT_EVENT = "abh:consent-change";

const DEFAULT_CONSENT = {
  necessary: true,
  statistics: false,
  marketing: false,
};

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.choices || null;
  } catch {
    return null;
  }
}

export function grantConsent(category) {
  const current = getConsent() || { necessary: true, statistics: false, marketing: false };
  const updated = { ...current, necessary: true, [category]: true };
  const record = {
    choices: updated,
    decidedAt: new Date().toISOString(),
    version: 1,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: updated }));
  return updated;
}

export const CONSENT_CHANGE_EVENT = CONSENT_EVENT;

function saveConsent(choices) {
  const record = {
    choices,
    decidedAt: new Date().toISOString(),
    version: 1,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: choices }));
}

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent("abh:open-cookie-settings"));
}

const CATEGORIES = [
  {
    key: "necessary",
    icon: Shield,
    title: "Technisch notwendig",
    locked: true,
    description:
      "Diese Cookies sind für den Betrieb der Website zwingend erforderlich (z. B. Sicherheit, Lastverteilung, Spracheinstellung, Cookie-Auswahl). Sie können nicht deaktiviert werden.",
  },
  {
    key: "statistics",
    icon: BarChart3,
    title: "Statistik & Reichweitenmessung",
    locked: false,
    description:
      "Anonymisierte Auswertung der Nutzung, um die Website kontinuierlich zu verbessern. Beispiel: Plausible, Google Analytics.",
  },
  {
    key: "marketing",
    icon: Megaphone,
    title: "Marketing & Drittinhalte",
    locked: false,
    description:
      "Eingebettete Inhalte (z. B. Social-Media-Embeds, Videos) sowie Marketing-Pixel. Werden erst nach deiner Einwilligung geladen.",
  },
];

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choices, setChoices] = useState(DEFAULT_CONSENT);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      setOpen(true);
    } else {
      setChoices({ ...DEFAULT_CONSENT, ...stored });
    }
    const onOpen = () => {
      const cur = getConsent() || DEFAULT_CONSENT;
      setChoices({ ...DEFAULT_CONSENT, ...cur });
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener("abh:open-cookie-settings", onOpen);
    return () => window.removeEventListener("abh:open-cookie-settings", onOpen);
  }, []);

  const acceptAll = () => {
    const c = { necessary: true, statistics: true, marketing: true };
    saveConsent(c);
    setChoices(c);
    setOpen(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const c = { necessary: true, statistics: false, marketing: false };
    saveConsent(c);
    setChoices(c);
    setOpen(false);
    setShowSettings(false);
  };

  const saveCustom = () => {
    saveConsent({ ...choices, necessary: true });
    setOpen(false);
    setShowSettings(false);
  };

  if (!open) {
    return (
      <button
        data-testid="cookie-settings-floater"
        onClick={() => {
          setShowSettings(true);
          setOpen(true);
        }}
        aria-label="Cookie-Einstellungen öffnen"
        className="fixed bottom-5 left-5 z-40 h-11 w-11 rounded-full bg-[#1F2937] text-[#F7F9FB] shadow-lg hover:bg-[#0F1825] border border-[#F7F9FB]/10 flex items-center justify-center transition"
      >
        <Cookie size={18} />
      </button>
    );
  }

  return (
    <div
      data-testid="cookie-consent"
      className="fixed inset-0 z-[60] flex items-end sm:items-end justify-center sm:justify-end p-0 sm:p-6 pointer-events-none"
    >
      <div className="pointer-events-auto w-full sm:max-w-md bg-[#F7F9FB] border-t sm:border border-[#1F2937]/15 sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[#2E89C4]/10 flex items-center justify-center shrink-0">
              <Cookie size={18} className="text-[#2E89C4]" />
            </div>
            <div className="flex-1">
              <h3 className="font-display font-bold text-lg text-[#1F2937]">
                Wir respektieren deine Privatsphäre
              </h3>
              <p className="text-sm text-[#1F2937]/70 mt-1 leading-relaxed">
                Diese Seite verwendet Cookies. Technisch notwendige Cookies sind
                immer aktiv. Andere setzen wir nur mit deiner Einwilligung ein.
                Du kannst deine Auswahl jederzeit über das Cookie-Symbol unten
                links ändern.
              </p>
            </div>
            {showSettings && (
              <button
                aria-label="Schließen"
                onClick={() => {
                  setShowSettings(false);
                  if (getConsent()) setOpen(false);
                }}
                className="text-[#1F2937]/60 hover:text-[#1F2937] transition"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {showSettings && (
            <div data-testid="cookie-settings-panel" className="mt-3 space-y-2">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const checked = !!choices[cat.key];
                const isOpen = expanded[cat.key];
                return (
                  <div
                    key={cat.key}
                    className="border border-[#1F2937]/10 rounded-xl overflow-hidden bg-white/60"
                  >
                    <div className="flex items-center gap-3 p-3.5">
                      <Icon size={16} className="text-[#6FB04F] shrink-0" />
                      <button
                        onClick={() =>
                          setExpanded((e) => ({ ...e, [cat.key]: !e[cat.key] }))
                        }
                        className="flex-1 text-left text-sm font-semibold text-[#1F2937]"
                      >
                        {cat.title}
                      </button>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          data-testid={`cookie-toggle-${cat.key}`}
                          type="checkbox"
                          className="sr-only peer"
                          checked={checked}
                          disabled={cat.locked}
                          onChange={(e) =>
                            !cat.locked &&
                            setChoices((c) => ({
                              ...c,
                              [cat.key]: e.target.checked,
                            }))
                          }
                        />
                        <div
                          className={`w-10 h-5 rounded-full transition ${
                            cat.locked
                              ? "bg-[#6FB04F]"
                              : checked
                              ? "bg-[#2E89C4]"
                              : "bg-[#1F2937]/20"
                          }`}
                        >
                          <div
                            className={`h-4 w-4 mt-0.5 rounded-full bg-white shadow transition ${
                              checked ? "ml-5" : "ml-0.5"
                            }`}
                          />
                        </div>
                      </label>
                      <button
                        aria-label="Details"
                        onClick={() =>
                          setExpanded((e) => ({ ...e, [cat.key]: !e[cat.key] }))
                        }
                        className="text-[#1F2937]/50"
                      >
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-3.5 -mt-1 text-xs text-[#1F2937]/65 leading-relaxed">
                        {cat.description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            {!showSettings && (
              <button
                data-testid="cookie-settings-button"
                onClick={() => setShowSettings(true)}
                className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#1F2937]/20 text-[#1F2937] hover:bg-[#1F2937]/5 transition"
              >
                Einstellungen
              </button>
            )}
            <button
              data-testid="cookie-reject-button"
              onClick={rejectAll}
              className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#1F2937]/20 text-[#1F2937] hover:bg-[#1F2937]/5 transition"
            >
              Nur notwendige
            </button>
            {showSettings ? (
              <button
                data-testid="cookie-save-button"
                onClick={saveCustom}
                className="px-4 py-2.5 rounded-full text-sm font-semibold flex-1 bg-[#1F2937] text-[#F7F9FB] hover:bg-[#0F1825] transition"
              >
                Auswahl speichern
              </button>
            ) : (
              <button
                data-testid="cookie-accept-button"
                onClick={acceptAll}
                className="px-4 py-2.5 rounded-full text-sm font-semibold flex-1 bg-[#2E89C4] text-[#F7F9FB] hover:bg-[#1F6FA1] transition"
              >
                Alle akzeptieren
              </button>
            )}
          </div>

          <p className="mt-3 text-[11px] text-[#1F2937]/50">
            Mehr Informationen in unserer{" "}
            <a
              href="/datenschutz"
              className="underline hover:text-[#2E89C4]"
            >
              Datenschutzerklärung
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
